import { Component } from "@theme/component";
import { throttle, isMobileBreakpoint, mediaQueryMobile } from "@theme/utilities";
import { Slider } from "@theme/slider";
import { setupCounter } from "@theme/carousel-features/counter";
import {
  setupNavigationPosition,
  performNavigationPositionCalculation,
} from "@theme/carousel-features/navigation-position";
import { setupThumbnails, destroyThumbnailSwiper } from "@theme/carousel-features/thumbnails";

/** Breakpoint (px) for mobile/desktop - aligns with theme media query (width <= 767px) */
const MOBILE_DESKTOP_BREAKPOINT = 768;

/**
 * <carousel-slider>, and what the theme's other carousels build on: it runs the theme's slider
 * (slider.js) on its container, with the options in its data-options or <script ref="config">,
 * which keep Swiper's names. Arrows, the progress bar and the counter are found by their refs, and
 * with destroyBelow / destroyAbove it stands down at those widths, leaving the slides to the CSS.
 */
export class CarouselComponent extends Component {
  /**
   * The carousel's slider. It's named for Swiper, which it replaced, as the components that use it
   * know it by that name.
   * @type {Slider | null}
   */
  swiperInstance = null;
  /** @type {Slider | null} The thumbnails' slider (thumbnails.js) */
  thumbnailSwiper = null;
  /** @type {string} */
  counterFormat = "{current} / {total}";
  /** @type {Record<string, any> | null} The options the slider was started with */
  lastResolvedOptions = null;
  /** @type {Record<string, any> | null} The options read at the last start, the slider's or not */
  finalOptions = null;

  /**
   * Enforce a container ref for the slider's root.
   * @type {string[]}
   */
  requiredRefs = ["container"];
  /** @type {IntersectionObserver | null} navigation-position.js's */
  navigationPositionObserver = null;
  /** @type {IntersectionObserver | null} */
  #nearObserver = null;
  /** Whether the slider has been started since the carousel was connected (see #initWhenNear) */
  #hasStarted = false;
  /** @type {boolean} */
  #isDestroyed = false;
  /** @type {(() => void) | null} */
  #resizeCleanup = null;
  /** @type {string | null} */
  #currentBreakpoint = null;
  /** @type {AbortController | undefined} */
  #shopifyAbortController;

  static get observedAttributes() {
    return ["data-options"];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#isDestroyed = false;
    if (this.swiperInstance) this.#destroySlider();
    this.#initWhenNear();
  }

  /**
   * Starts the slider once the carousel is drawn within a screen of the viewport. Starting one
   * measures every slide, so a carousel further down the page, or in a drawer or dialog that's
   * closed (the cart and search drawers are on every page), waits until it's about to be seen. One
   * on screen starts in the first frame.
   */
  #initWhenNear() {
    this.#nearObserver?.disconnect();
    this.#nearObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#nearObserver?.disconnect();
        this.#nearObserver = null;
        this.#hasStarted = true;
        this.#initSlider();
      },
      { rootMargin: "100% 0px" }
    );
    this.#nearObserver.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    // Not started yet: it starts from the new markup when it comes near
    if (!this.#hasStarted) return;
    requestAnimationFrame(() => {
      if (!this.refs?.container) return;
      this.#destroySlider();
      this.#initSlider();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#isDestroyed = true;
    this.#nearObserver?.disconnect();
    this.#nearObserver = null;
    this.#hasStarted = false;
    this.#destroySlider();
    this.#resizeCleanup?.();
    this.#resizeCleanup = null;
    this.navigationPositionObserver?.disconnect();
    this.navigationPositionObserver = null;
  }

  attributeChangedCallback(name, _oldValue, _newValue) {
    // Before it has started (this also runs as the element is upgraded) the start reads the options
    if (name === "data-options" && this.#hasStarted) {
      this.#destroySlider();
      requestAnimationFrame(() => this.#initSlider());
    }
  }

  get slideClass() {
    return this.finalOptions?.slideClass || "swiper-slide";
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  /**
   * Navigates to a specific slide, and stops autoplay.
   * @param {number} index - The slide index to navigate to
   * @param {number} [speed] - Transition speed in ms; 0 goes there at once. Without it, the slider's
   * @param {boolean} [runCallbacks] - Whether to announce the change's start. Defaults to true
   */
  goToSlide(index, speed, runCallbacks = true) {
    const slider = this.swiperInstance;
    if (!slider || slider.destroyed) return;
    if (slider.autoplay.running) slider.autoplay.pause();
    slider.slideTo(index, speed, runCallbacks);
  }

  #initSlider() {
    if (this.swiperInstance) return;
    if (!this.refs.container) return;

    this.#currentBreakpoint = isMobileBreakpoint() ? "mobile" : "desktop";
    const options = this.#resolveElementSelectors(this.#parseOptions());
    this.finalOptions = options;
    this.#setupResizeHandler(options);

    if (this.#shouldDestroyForViewport(options)) {
      this.#setControlsHidden(true, options);
      return;
    }
    this.#setControlsHidden(false, options);
    this.#createSlider(options);
  }

  /** @param {Record<string, any>} options */
  #createSlider(options) {
    const container = /** @type {HTMLElement | undefined} */ (this.refs.container);
    if (!container) return;
    if (!container.querySelector(`.${this.slideClass}`)) {
      this.#setControlsHidden(true, options);
      return;
    }

    const slider = new Slider(container, options);
    this.swiperInstance = slider;
    this.lastResolvedOptions = options;

    setupCounter(this, options);
    if (options.thumbnails?.enabled) setupThumbnails(this, options.thumbnails);
    setupNavigationPosition(this, options);
    // Nothing to move to (the slides fit): no controls
    this.#toggleControlsForOverflow();
    slider.on("lock unlock", () => this.#toggleControlsForOverflow());
    this.#registerDesignModeEvents();

    this.dispatchEvent(
      new CustomEvent("carousel:ready", {
        bubbles: false,
        detail: { swiperInstance: slider },
      })
    );
  }

  #destroySlider() {
    this.swiperInstance?.destroy();
    this.swiperInstance = null;
    destroyThumbnailSwiper(this);
    this.lastResolvedOptions = null;
    this.#shopifyAbortController?.abort();
    this.#shopifyAbortController = undefined;
  }

  #parseOptions() {
    const attr = this.getAttribute("data-options");
    if (attr) {
      try {
        return JSON.parse(attr);
      } catch (_e) {
        console.error("CarouselComponent: invalid JSON in data-options");
      }
    }

    const script = this.querySelector('script[ref="config"][type="application/json"]');
    if (script && script.textContent) {
      try {
        return JSON.parse(script.textContent);
      } catch (_e) {
        console.error('CarouselComponent: invalid JSON in <script ref="config"> content');
      }
    }

    return {};
  }

  #resolveElementSelectors(options) {
    const resolved = { ...options };

    const resolve = (selectorOrEl) => {
      if (typeof selectorOrEl !== "string") return selectorOrEl;
      try {
        return this.querySelector(selectorOrEl);
      } catch (_e) {
        return selectorOrEl;
      }
    };

    const byRef = (name) => {
      const isMobile = isMobileBreakpoint();
      const targetBreakpoint = isMobile ? "mobile" : "desktop";
      const targetContainer = this.querySelector(`[data-controls-breakpoint="${targetBreakpoint}"]`);

      if (targetContainer) {
        const computedStyle = window.getComputedStyle(targetContainer);
        if (computedStyle.display !== "none" && computedStyle.visibility !== "hidden") {
          const el = targetContainer.querySelector(`[data-ref="${name}"]`);
          if (el) {
            el.setAttribute("ref", name);
            return /** @type {HTMLElement} */ (el);
          }
        }
      }

      const fallbackEl = this.querySelector(`[ref="${name}"]`);
      if (fallbackEl) {
        const parentContainer = fallbackEl.closest("[data-controls-breakpoint]");
        if (parentContainer) {
          const computedStyle = window.getComputedStyle(parentContainer);
          if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
            return null;
          }
        }
      }
      return /** @type {HTMLElement | null} */ (fallbackEl);
    };

    const paginationRef = byRef("pagination");
    if (resolved.pagination === true) {
      resolved.pagination = {};
    }
    if (!resolved.pagination && paginationRef) {
      resolved.pagination = { el: paginationRef };
    } else if (resolved.pagination && typeof resolved.pagination === "object") {
      const el = resolved.pagination.el ?? paginationRef;
      resolved.pagination = { ...resolved.pagination, el: resolve(el) };
    }

    if (resolved.navigation === true) {
      resolved.navigation = {};
    }
    if (resolved.navigation && typeof resolved.navigation === "object") {
      const next = resolved.navigation.nextEl ?? byRef("next");
      const prev = resolved.navigation.prevEl ?? byRef("prev");
      resolved.navigation = {
        ...resolved.navigation,
        nextEl: resolve(next),
        prevEl: resolve(prev),
      };
    } else {
      const next = byRef("next");
      const prev = byRef("prev");
      if (next || prev) {
        resolved.navigation = {
          nextEl: next ?? undefined,
          prevEl: prev ?? undefined,
        };
      }
    }

    const counterRef = byRef("counter");
    const hasCounterOption = Object.prototype.hasOwnProperty.call(options, "counter");
    if (resolved.counter === true) {
      resolved.counter = {};
    }
    if (typeof resolved.counter === "string") {
      resolved.counter = { format: resolved.counter };
    }
    if (!hasCounterOption && counterRef) {
      resolved.counter = { el: counterRef, format: this.counterFormat };
    } else if (resolved.counter && typeof resolved.counter === "object") {
      const el = resolved.counter.el ?? counterRef;
      resolved.counter = {
        ...resolved.counter,
        el: resolve(el),
        format: typeof resolved.counter.format === "string" ? resolved.counter.format : this.counterFormat,
      };
    }

    if (resolved.navigationPosition && typeof resolved.navigationPosition === "object") {
      resolved.navigationPosition = {
        enabled: resolved.navigationPosition.enabled !== false,
        selector: resolved.navigationPosition.selector || "img",
        offset: Number(resolved.navigationPosition.offset) || 0,
        fallback: resolved.navigationPosition.fallback || "center",
        ...resolved.navigationPosition,
      };
    }

    return resolved;
  }

  #shouldDestroyForViewport(options) {
    const w = window.innerWidth || 0;
    const below = Number(options.destroyBelow);
    const above = Number(options.destroyAbove);
    const isMobile = isMobileBreakpoint();

    const useMediaQuery = (val) => val === MOBILE_DESKTOP_BREAKPOINT;
    const shouldDestroyBelow = !Number.isNaN(below) && (useMediaQuery(below) ? isMobile : w < below);
    const shouldDestroyAbove = !Number.isNaN(above) && (useMediaQuery(above) ? !isMobile : w >= above);
    return shouldDestroyBelow || shouldDestroyAbove;
  }

  #setControlsHidden(hidden, options) {
    const toggle = (el) => {
      if (!(el instanceof HTMLElement)) return;
      el.classList.toggle("hidden-control", hidden);
    };

    const hasResponsiveControls = this.querySelector("[data-controls-breakpoint]");

    if (!hasResponsiveControls) {
      const controlsEl = this.querySelector('[ref="controls"]');
      if (controlsEl instanceof HTMLElement) {
        controlsEl.classList.toggle("hidden", hidden);
      }

      const controlsWrapper = this.querySelector(".carousel__controls");
      if (controlsWrapper instanceof HTMLElement) {
        controlsWrapper.classList.toggle("hidden-control", hidden);
      }
    }

    // Pagination
    const pagEl = options?.pagination?.el || this.querySelector('[ref="pagination"]');
    if (options && Object.prototype.hasOwnProperty.call(options, "pagination")) {
      toggle(pagEl);
    }

    // Navigation
    const nextEl = options?.navigation?.nextEl || this.querySelector('[ref="next"]');
    const prevEl = options?.navigation?.prevEl || this.querySelector('[ref="prev"]');
    if (options && Object.prototype.hasOwnProperty.call(options, "navigation")) {
      toggle(nextEl);
      toggle(prevEl);
    }

    const counterEl = this.querySelector('[ref="counter"]');
    if (options && Object.prototype.hasOwnProperty.call(options, "counter")) {
      const shouldHide = hidden || !options.counter;
      if (counterEl instanceof HTMLElement) {
        counterEl.classList.toggle("hidden", shouldHide);
      }
    }

    // Thumbnails
    const thumbnailContainer = this.querySelector('[ref="thumbnails"]');
    if (thumbnailContainer && options?.thumbnails?.enabled) {
      thumbnailContainer.classList.toggle("hidden", hidden);
    }
  }

  /** Hides the controls while the slides all fit, as there's nowhere for them to go */
  #toggleControlsForOverflow() {
    const controlsEl = this.querySelector('[ref="controls"]');
    if (!controlsEl || !this.swiperInstance) return;
    controlsEl.classList.toggle("hidden-control", this.swiperInstance.isLocked);
  }

  /**
   * Stands the slider down at the widths destroyBelow / destroyAbove leave to the CSS, and starts it
   * again past them. A carousel with controls of its own for phones and desktops starts again across
   * that breakpoint, so it has the ones shown. The slider follows its own breakpoints.
   * @param {Record<string, any>} options
   */
  #setupResizeHandler(options) {
    this.#resizeCleanup?.();
    this.#resizeCleanup = null;
    this.#currentBreakpoint = isMobileBreakpoint() ? "mobile" : "desktop";

    const runViewportCheck = () => {
      if (this.#isDestroyed) return;
      const fresh = this.#resolveElementSelectors(this.#parseOptions());
      const breakpoint = isMobileBreakpoint() ? "mobile" : "desktop";
      const breakpointChanged = breakpoint !== this.#currentBreakpoint;
      this.#currentBreakpoint = breakpoint;

      if (this.#shouldDestroyForViewport(fresh)) {
        if (this.swiperInstance) this.#destroySlider();
        this.#setControlsHidden(true, fresh);
        return;
      }

      if (this.swiperInstance && breakpointChanged && this.querySelector("[data-controls-breakpoint]")) {
        this.#destroySlider();
      }
      if (!this.swiperInstance) {
        this.finalOptions = fresh;
        this.#setControlsHidden(false, fresh);
        this.#createSlider(fresh);
        return;
      }

      if (fresh.navigationPosition?.enabled) performNavigationPositionCalculation(this, fresh);
    };

    const handleResize = throttle(runViewportCheck, 150);
    window.addEventListener("resize", handleResize);

    const watchesMobile =
      Number(options?.destroyAbove) === MOBILE_DESKTOP_BREAKPOINT ||
      Number(options?.destroyBelow) === MOBILE_DESKTOP_BREAKPOINT ||
      !!this.querySelector("[data-controls-breakpoint]");
    if (watchesMobile) mediaQueryMobile.addEventListener("change", runViewportCheck);

    this.#resizeCleanup = () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel?.();
      if (watchesMobile) mediaQueryMobile.removeEventListener("change", runViewportCheck);
    };
  }

  #registerDesignModeEvents() {
    if (!this.finalOptions?.shopifyBlocks || !Shopify.designMode || !this.sectionId) return;

    this.#shopifyAbortController?.abort();
    this.#shopifyAbortController = new AbortController();
    const { signal } = this.#shopifyAbortController;

    document.addEventListener(
      "shopify:block:select",
      (event) => {
        if (event.detail.sectionId != this.sectionId) return;

        const slideEl = event.target.closest(`.${this.slideClass}`);
        const index = this.swiperInstance?.slides.indexOf(slideEl) ?? -1;
        if (index !== -1) this.goToSlide(index);
      },
      { signal }
    );
  }
}

if (!customElements.get("carousel-slider")) {
  customElements.define("carousel-slider", CarouselComponent);
}

export { CarouselComponent as default };
